import {
  ARCHIVE_ROOT,
  VaultError,
  isAttachment,
  isEncryptedAttachment,
  isEncryptedFile,
  isEncryptedNote,
  isFolderKey,
} from "./model";
export function relativePath(value: string): string {
  if (
    typeof value !== "string" ||
    !value ||
    value.length > 1024 ||
    /[\x00-\x1f\x7f\\]/.test(value) ||
    value.startsWith("/") ||
    value.endsWith("/")
  )
    throw new VaultError("invalid_vault", "Use a safe relative path.");
  if (
    value
      .split("/")
      .some(
        (s) =>
          !s.trim() ||
          s === "." ||
          s === ".." ||
          s.toLowerCase() === ".turbonotes" ||
          s.toLowerCase() === ".git",
      )
  )
    throw new VaultError(
      "invalid_vault",
      "Empty, reserved, and traversal path segments are not allowed.",
    );
  return value;
}
export const under = (path: string, root: string): boolean =>
  path.startsWith(root + "/");
export const isMetadataPath = (path: string): boolean =>
  path.split("/").some((segment) => segment.toLowerCase() === ".turbonotes");
export const isNote = (path: string): boolean =>
  (path.endsWith(".md") || isEncryptedNote(path)) && !isMetadataPath(path);
export const isFolderMarker = (path: string): boolean =>
  path.endsWith("/.gitkeep") && !isMetadataPath(path);
/** Every kind of file the vault reads from and writes to the repository. */
export const isVaultFile = (path: string): boolean =>
  !isMetadataPath(path) &&
  (isNote(path) ||
    isFolderMarker(path) ||
    isAttachment(path) ||
    isEncryptedAttachment(path) ||
    isFolderKey(path));
export function vaultFilePath(path: string): string {
  if (
    isFolderMarker(path) ||
    isAttachment(path) ||
    isEncryptedAttachment(path) ||
    isFolderKey(path)
  )
    return relativePath(path);
  return notePath(path);
}
/** The folders that hold a folder key, from a list of vault paths. */
export function keyFolders(paths: Iterable<string>): Set<string> {
  const folders = new Set<string>();
  for (const path of paths)
    if (isFolderKey(path) && !isMetadataPath(path)) folders.add(folderOf(path));
  return folders;
}
/**
 * The encrypted folder whose key covers a folder: the folder itself or its nearest
 * ancestor holding a key. Archiving prefixes a note's path, so an archived note keeps
 * the key of the folder it came from.
 */
export function encryptedFolderOf(
  folder: string,
  keys: ReadonlySet<string>,
): string | null {
  const find = (start: string) => {
    for (let current = start; current; current = folderOf(current))
      if (keys.has(current)) return current;
    return null;
  };
  return (
    find(folder) ??
    (under(folder, ARCHIVE_ROOT) || folder === ARCHIVE_ROOT
      ? find(folder.slice(ARCHIVE_ROOT.length + 1))
      : null)
  );
}
/**
 * Why a note can't move between these paths, or null when it can. An encrypted note
 * stays under its folder's key, in or out of the archive. A plain note moved into an
 * encrypted folder is encrypted from then on; its earlier commits stay as they were.
 */
export function moveProblem(
  source: string,
  destination: string,
  keys: ReadonlySet<string>,
): string | null {
  const from = encryptedFolderOf(folderOf(source), keys),
    to = encryptedFolderOf(folderOf(destination), keys);
  if (isEncryptedNote(source)) {
    if (!isEncryptedNote(destination)) return "Moving a note can't decrypt it.";
    if (from !== to)
      return `Encrypted notes stay in “${from}”. Move them within it or to the archive.`;
    return null;
  }
  if (isEncryptedNote(destination) && to === null)
    return "Only a note in an encrypted folder can be encrypted.";
  if (!isEncryptedNote(destination) && to !== null)
    return `A note moved into “${to}” is encrypted, so it needs the .md.age extension.`;
  return null;
}
/** Where a path ends up once the folder `source` is renamed to `destination`. */
export const movedInto = (path: string, source: string, destination: string): string =>
  path === source || under(path, source) ? destination + path.slice(source.length) : path;
/** A folder of images that notes link to, which stays where it is. */
const isImageFolder = (folder: string): boolean => folder.split("/").at(-1) === "attachments";
/**
 * Why `source` can't be renamed to `destination`, or deleted when that is null; null
 * when it can. `paths` are the vault's files and `folders` every folder, empty ones
 * included. A folder takes everything in it along, an encrypted one its key too, so the
 * rules are that every encrypted file keeps the key it has and no plain note lands in an
 * encrypted folder, where it would have to be encrypted one note at a time.
 */
export function folderProblem(
  source: string,
  destination: string | null,
  paths: readonly string[],
  folders: readonly string[],
): string | null {
  if (!source) return "The repository root can't be renamed or deleted.";
  if (!folders.includes(source)) return `There is no folder “${source}”.`;
  if (isImageFolder(source))
    return `Notes link to the images in “${source}”, so it can't be renamed or deleted.`;
  if (destination !== null) {
    if (destination === source) return null;
    if (source === ARCHIVE_ROOT)
      return "The archive keeps its name, so archived notes know where they came from.";
    if (under(destination, source)) return "A folder can't move inside itself.";
    if (
      folders.includes(destination) ||
      paths.some((path) => path === destination || under(path, destination) || under(destination, path))
    )
      return `“${destination}” already exists.`;
  }
  const inside = (path: string) => under(path, source);
  const after = (path: string) => (destination === null ? path : movedInto(path, source, destination));
  const kept = destination === null ? paths.filter((path) => !inside(path)) : paths;
  const keys = keyFolders(paths),
    nextKeys = keyFolders(kept.map(after));
  for (const key of nextKeys) {
    if (key === ARCHIVE_ROOT || under(key, ARCHIVE_ROOT))
      return "An encrypted folder can't be inside the archive.";
    const outer = encryptedFolderOf(folderOf(key), nextKeys);
    if (outer !== null) return `“${outer}” is already encrypted with its own key.`;
  }
  for (const path of kept) {
    const before = encryptedFolderOf(folderOf(path), keys),
      next = encryptedFolderOf(folderOf(after(path)), nextKeys);
    if (isEncryptedFile(path)) {
      if (before === null || next === after(before)) continue;
      if (inside(path)) return `Encrypted notes stay inside “${before}”.`;
      return destination === null
        ? `“${before}”'s key also opens ${path}. Delete or unarchive it first.`
        : `“${before}”'s key also opens ${path}, which can't follow it. Unarchive it first.`;
    } else if (isNote(path) && before === null && next !== null)
      return `Plain notes can't move into the encrypted folder “${next}” together. Drag them in one at a time.`;
  }
  return null;
}
/** The extension a new note in this folder takes. */
export const noteExtension = (folder: string, keys: ReadonlySet<string>): string =>
  encryptedFolderOf(folder, keys) === null ? ".md" : ".md.age";
export function foldersFromPaths(
  paths: Iterable<string>,
  existing: Iterable<string> = [],
): string[] {
  const folders = new Set<string>();
  const add = (folder: string) => {
    if (isMetadataPath(folder)) return;
    while (folder) {
      folders.add(relativePath(folder));
      folder = folderOf(folder);
    }
  };
  for (const folder of existing) add(folder);
  for (const path of paths) if (!isMetadataPath(path)) add(folderOf(path));
  return [...folders].sort((a, b) => a.localeCompare(b));
}
export function notePath(path: string): string {
  relativePath(path);
  if (!path.endsWith(".md") && !isEncryptedNote(path))
    throw new VaultError("invalid_vault", "Notes must be Markdown files.");
  if (!titleOf(path).trim())
    throw new VaultError("invalid_vault", "A note needs a filename.");
  return path;
}
/** A note's path without its extension, the form wiki links use. */
export const noteStem = (path: string): string => path.replace(/\.md(?:\.age)?$/, "");
export const titleOf = (path: string): string => noteStem(path.split("/").at(-1)!);
export const folderOf = (path: string): string =>
  path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";
export function assertDestination(
  path: string,
  paths: Iterable<string>,
  except?: string,
): void {
  for (const other of paths)
    if (
      other !== except &&
      (other === path || under(other, path) || under(path, other))
    )
      throw new VaultError("conflict", `Destination is occupied: ${other}`);
}
const colorNames =
  "aliceblue antiquewhite aqua aquamarine azure beige bisque black blanchedalmond blue blueviolet brown burlywood cadetblue chartreuse chocolate coral cornflowerblue cornsilk crimson cyan darkblue darkcyan darkgoldenrod darkgray darkgreen darkgrey darkkhaki darkmagenta darkolivegreen darkorange darkorchid darkred darksalmon darkseagreen darkslateblue darkslategray darkslategrey darkturquoise darkviolet deeppink deepskyblue dimgray dimgrey dodgerblue firebrick floralwhite forestgreen fuchsia gainsboro ghostwhite gold goldenrod gray green greenyellow grey honeydew hotpink indianred indigo ivory khaki lavender lavenderblush lawngreen lemonchiffon lightblue lightcoral lightcyan lightgoldenrodyellow lightgray lightgreen lightgrey lightpink lightsalmon lightseagreen lightskyblue lightslategray lightslategrey lightsteelblue lightyellow lime limegreen linen magenta maroon mediumaquamarine mediumblue mediumorchid mediumpurple mediumseagreen mediumslateblue mediumspringgreen mediumturquoise mediumvioletred midnightblue mintcream mistyrose moccasin navajowhite navy oldlace olive olivedrab orange orangered orchid palegoldenrod palegreen paleturquoise palevioletred papayawhip peachpuff peru pink plum powderblue purple rebeccapurple red rosybrown royalblue saddlebrown salmon sandybrown seagreen seashell sienna silver skyblue slateblue slategray slategrey snow springgreen steelblue tan teal thistle tomato turquoise violet wheat white whitesmoke yellow yellowgreen".split(
    " ",
  );
const colors = new Map(colorNames.map((name) => [name, name]));
export const folderColor = (path: string): string =>
  colors.get(folderOf(path).split("/").at(-1)!.toLowerCase()) ?? "transparent";
export function resolveWikiLink(link: string, paths: string[]): string[] {
  const active = paths.filter((p) => isNote(p) && !under(p, ARCHIVE_ROOT));
  if (link.includes("/")) return active.filter((p) => noteStem(p) === link);
  return active.filter((p) => titleOf(p) === link);
}
